<script setup lang="ts">
import { Head, Link, router } from '@inertiajs/vue3'
import PageHeading from "@/Components/Headings/PageHeading.vue"
import { PageHeading as PageHeadingTS } from '@/types/PageHeading'
import Table from '@/Components/Table/Table.vue'
import { useFormatTime } from '@/Composables/useFormatTime'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { faStickyNote } from '@fal'
import { useTruncate } from '@/Composables/useTruncate'
import { library } from '@fortawesome/fontawesome-svg-core'
import Button from '@/Components/Utils/Button.vue'
import { ref } from 'vue'
import Icon from '@/Components/Utils/Icon.vue'
import Tag from '@/Components/Utils/Tag.vue'
import { inject } from 'vue'
import { layoutStructure } from '@/Composables/useLayoutStructure'
library.add(faStickyNote)

const props = defineProps<{
    pageHead?: PageHeadingTS
    title?: string
    data: {}
}>()

const layout = inject('layout', layoutStructure)

const isLoading = ref<number | boolean>(false)
const onSetBillingPaid = (order_id: number) => {
    router.get(
        route('sales.orders.set-paid', order_id),
        {},
        {
            onStart: () => isLoading.value = order_id,
            onFinish: () => isLoading.value = false,
        }
    )
}

const generateLinkReference = (order: { slug: string }) => {
    if (route().current()?.includes('master.orders-m')) {  // Master
        return route('master.orders-m.show', order.slug)
    } else {
        return route('sales.orders.show', order.slug)
    }
}

const generateLinkProduct = (productSlug: string) => {
    if (!productSlug) {
        return ''
    }

    if (layout.auth.role === 'super_admin' || layout.auth.role === 'admin') {
        return route('casheer.products.show', productSlug)
    } else {
        return ''
    }
}

// Section: Generate link brand
const generateLinkBrand = (brand: {}) => {
    if (!brand?.slug) {
        return ''
    }

    if (layout.auth.role === 'sales' || layout.auth.role === 'sales_special') {
        return route('sales.brands.show', brand?.slug)
    } else {
        return ''
    }
}
</script>

<template>
    <Head :title="title" />
    <PageHeading :data="pageHead" />
    <Table :resource="data">

        <!-- Reference -->
        <template #cell(reference)="{ item }">
            <div>
                <Link :href="generateLinkReference(item)" class="primaryLink">{{ item.reference }}</Link>
                <!-- {{ generateLinkReference(item) }} -->
                <div class="px-0 text-gray-400">
                    <FontAwesomeIcon v-tooltip="'Catatan tambahan'" icon='fal fa-sticky-note' class='' fixed-width aria-hidden='true' />
                    {{ useTruncate(item.note || '-', 30) }}
                </div>

            </div>

            <div v-if="item.child_order" class="mt-2 pl-2 border-l-4 py-1 border-fuchsia-400 bg-fuchsia-500/10">
                Tambahan: <Icon :data="item.child_order.type_icon" />
                <Link v-if="(layout.auth.role === 'super_admin' || layout.auth.role === 'admin')" :href="generateLinkReference(item.child_order)" class="secondaryLink">
                    {{ item.child_order.reference }}
                </Link>
                <span v-else>
                    {{ item.child_order.reference }}
                </span>
            </div>
            
            <div v-if="item.manufacture?.state_label" class="px-0 text-gray-400" :class="item.manufacture?.state_icon?.class">
                <FontAwesomeIcon v-tooltip="'Status manufaktur'" icon='fal fa-shredder' class='' fixed-width aria-hidden='true' />
                {{ item.manufacture.state_label }}
            </div>
        </template>

        <!-- Created at -->
        <template #cell(created_at)="{ item }">
            {{ useFormatTime(item.created_at) }}
        </template>

        <!-- Brand -->
        <template #cell(brand)="{ item }">
            <div class="flex items-center gap-x-1">
                <Link v-if="generateLinkBrand(item.brand)" :href="generateLinkBrand(item.brand)" class="secondaryLink">
                    {{ item.brand?.name }}
                </Link>
                <div v-else-if="item.brand?.slug">
                    {{ item.brand?.name }}
                </div>
                <div v-else class="italic text-gray-400">
                    Brand tidak ditemukan
                </div>
            </div>

            <div v-if="item.customer?.name" class="font-normal text-gray-400">({{ item.customer?.name }})</div>
        </template>

        <!-- Sales name -->
        <template #cell(sales)="{ item }">
            <div class="flex items-center gap-x-1">
                <!-- <Icon :data="item.type_icon" /> -->
                <!-- {{ item.sales?.username }} -->
                <Link :href="route('master.sales-m.index.show', item.sales?.username)" class="secondaryLink">
                    {{ item.sales?.name }}
                </Link>
            </div>
        </template>

        <!-- Product -->
        <template #cell(product_name)="{ item }">
            <div class="flex items-center gap-x-1">
                <Icon :data="item.type_icon" />
                <Link v-if="(layout.auth.role === 'super_admin' || layout.auth.role === 'admin') && item.product_slug" :href="generateLinkProduct(item.product_slug)" class="secondaryLink">
                    {{ item.product?.name }}<span v-if="item.type === 'flexo' && item.dimension_width && item.dimension_height" class="text-gray-400 font-normal"> ({{ item.dimension_width }} × {{ item.dimension_height }} cm)</span>
                </Link>
                <div v-else-if="item.product?.name">
                    {{ item.product.name }}<span v-if="item.type === 'flexo' && item.dimension_width && item.dimension_height" class="text-gray-400 font-normal"> ({{ item.dimension_width }} × {{ item.dimension_height }} cm)</span>
                </div>
                <div v-else class="italic text-gray-400">
                    Produk tidak ditemukan
                </div>
            </div>

            <div v-if="item.child_order" class="mt-2 pl-2 border-l-4 py-1 border-fuchsia-400 bg-fuchsia-500/10">
                Tambahan: <Icon :data="item.child_order.type_icon" />
                <Link v-if="(layout.auth.role === 'super_admin' || layout.auth.role === 'admin') && item.child_order.product_slug" :href="generateLinkProduct(item.child_order.product_slug)" class="secondaryLink">
                    {{ item.child_order.product?.name }} ({{ item.child_order.quantity }} pcs)
                </Link>
                <span v-else-if="item.child_order.product?.name">
                    {{ item.child_order.product?.name }} ({{ item.child_order?.quantity }} pcs)
                </span>
                <div v-else class="italic text-gray-400">
                    Produk tidak ditemukan
                </div>
            </div>
        </template>

        <!-- Action -->
        <!-- <template #cell(action)="{ item }">
            <Button
                v-if="item.billing_status == 'unpaid'"
                @click="() => onSetBillingPaid(item.id)"
                label="Lunaskan"
                :loading="isLoading === item.id"
                class=""
            />
            <div v-else v-tooltip="'Lunas'" class="text-center">
                <FontAwesomeIcon icon='fal fa-check-double' class='text-green-500' fixed-width aria-hidden='true' />
            </div>
        </template> -->

        <template #cell(billing_status_label)="{ item }">
            <div v-if="item.manufacture_state !== 'canceled' && !item.canceled_at" v-tooltip="item.billing_date" class="">
                <Tag :label="item.billing_status_label" :theme="item.billing_date ? 3 : 7" noHoverColor /> <span v-if="item.billing_date" class="text-gray-400">({{ useFormatTime(item.billing_date) }})</span>
            </div>

            <div v-else>

            </div>
        </template>
    </Table>

</template>